import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Badge } from '@shared/components/ui/badge';
import { useIsMobile } from '@shared/hooks/useMobile';
import { usePreparationStatusName, getPreparationStatusColor } from '@modules/OrderPreparation/shared/config';

// 🎯 Props para el componente Breadcrumb compartido
export interface PreparationBreadcrumbProps {
  preparationCode: string;
  preparationStatus: number;
  breadcrumbLabel: string;
  basePath?: string;
  baseLabel?: string;
  showBaseBreadcrumb?: boolean;
}

// 🎯 Componente Breadcrumb compartido para preparaciones
export const PreparationBreadcrumb = ({
  preparationCode,
  preparationStatus,
  breadcrumbLabel,
  basePath = '/order-preparation',
  baseLabel,
  showBaseBreadcrumb = true,
}: PreparationBreadcrumbProps) => {
  const { t } = useTranslation('orderpreparationModule');
  const isMobile = useIsMobile();
  const statusName = usePreparationStatusName(preparationStatus);
  const statusColor = getPreparationStatusColor(preparationStatus);

  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [
      // Solo mostrar el breadcrumb base si NO es mobile y showBaseBreadcrumb es true
      ...(isMobile || !showBaseBreadcrumb
        ? []
        : [
            {
              label: baseLabel || t('breadcrumb.order_preparation'),
              path: basePath,
            },
          ]),
      {
        label: (
          <div className="flex items-center gap-3 text-title">
            <span>
              {breadcrumbLabel} {preparationCode}
            </span>
            <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
              {statusName}
            </Badge>
          </div>
        ),
      },
    ];

    return baseBreadcrumbs;
  }, [t, isMobile, showBaseBreadcrumb, baseLabel, basePath, breadcrumbLabel, preparationCode, statusName, statusColor]);

  usePageNavigation({ breadcrumbs });

  return null;
};
